import type {
  RuntimeCronJobRecordDto,
  RuntimeCronScopeKind,
} from "@/lib/api/generated/control-api.schemas";

export type RuntimeCronScope = RuntimeCronScopeKind;

export type RuntimeCronJobRecord = RuntimeCronJobRecordDto & {
  cronJobId: string;
  tenantId: string;
  name: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  schedule: NonNullable<RuntimeCronJobRecordDto["schedule"]>;
  payload: NonNullable<RuntimeCronJobRecordDto["payload"]>;
  sessionTarget: string;
  wakeMode: string;
  scopeKind: RuntimeCronScope;
  ownerUserId?: string | null;
};

export type RuntimeCronEditorState = {
  name: string;
  description: string;
  enabled: boolean;
  deleteAfterRun: boolean;
  sessionTarget: string;
  wakeMode: string;
  schedule: {
    kind: "at" | "every" | "cron";
    at: string;
    everyMs: number;
    anchorMs?: number;
    expr: string;
    tz: string;
    staggerMs?: number;
  };
  payload: {
    kind: "systemEvent" | "agentTurn" | "workflowRun";
    text: string;
    message: string;
    model: string;
    fallbacksCsv: string;
    thinking: string;
    timeoutSeconds?: number;
    allowUnsafeExternalContent: boolean;
    lightContext: boolean;
    deliver: boolean;
    channel: string;
    to: string;
    bestEffortDeliver: boolean;
    workflowKey: string;
    inputJson: string;
    inputSummary: string;
    executionClass: string;
  };
  deliveryEnabled: boolean;
  delivery: {
    mode: "none" | "announce" | "webhook";
    channel: string;
    to: string;
    accountId: string;
    bestEffort: boolean;
    failureDestinationEnabled: boolean;
    failureDestination: {
      channel: string;
      to: string;
      accountId: string;
      mode: "" | "announce" | "webhook";
    };
  };
  failureAlertEnabled: boolean;
  failureAlert: {
    after?: number;
    channel: string;
    to: string;
    cooldownMs?: number;
    mode: "" | "announce" | "webhook";
    accountId: string;
  };
};

export function createRuntimeCronDraft(tenantId: string, scopeKind: RuntimeCronScope): RuntimeCronJobRecord {
  const now = new Date().toISOString();
  return {
    cronJobId: crypto.randomUUID(),
    tenantId,
    name: scopeKind === "user" ? "New personal schedule" : "New tenant schedule",
    description: "",
    enabled: true,
    createdAt: now,
    updatedAt: now,
    schedule: {
      kind: "cron",
      expr: "0 9 * * *",
      tz: "UTC",
    },
    sessionTarget: "isolated",
    wakeMode: "now",
    payload: {
      kind: "agentTurn",
      message: "",
    },
    scopeKind,
    ownerUserId: null,
  };
}

export function getCronScope(job: RuntimeCronJobRecordDto): RuntimeCronScope {
  return job.scopeKind === "user" ? "user" : "tenant";
}

export function getCronOwnerUserId(job: RuntimeCronJobRecordDto): string | null {
  const ownerUserId = job.ownerUserId;
  return typeof ownerUserId === "string" && ownerUserId.trim().length > 0 ? ownerUserId : null;
}

export function requireCronJobId(job: RuntimeCronJobRecordDto): string {
  return requireNonEmptyString(job.cronJobId, "cronJobId");
}

export function describeSchedule(job: RuntimeCronJobRecordDto): string {
  const kind = typeof job.schedule?.kind === "string" ? job.schedule.kind : "";
  switch (kind) {
    case "at":
      return `At ${String(job.schedule?.at ?? "")}`;
    case "every":
      return `Every ${Math.round(Number(job.schedule?.everyMs ?? 0) / 1000)}s`;
    case "cron":
      return job.schedule?.tz
        ? `${String(job.schedule?.expr ?? "")} (${String(job.schedule?.tz)})`
        : String(job.schedule?.expr ?? "");
    default:
      return "Unknown";
  }
}

export function mapRunStatus(status: string): "running" | "error" | "ready" {
  switch (status) {
    case "ok":
      return "ready";
    case "error":
      return "error";
    default:
      return "running";
  }
}

export function createRuntimeCronEditorState(job: RuntimeCronJobRecordDto): RuntimeCronEditorState {
  const schedule = job.schedule ?? { kind: "every", everyMs: 60_000 };
  const payload = job.payload ?? { kind: "systemEvent", text: "" };

  const scheduleKind = readKind(schedule.kind, ["at", "every", "cron"], "schedule.kind");
  const scheduleAt = scheduleKind === "at" ? String(schedule.at ?? "") : "";
  const scheduleEveryMs = scheduleKind === "every" ? Number(schedule.everyMs ?? 0) : 0;
  const scheduleAnchorMs =
    scheduleKind === "every" && typeof schedule.anchorMs === "number"
      ? schedule.anchorMs
      : undefined;
  const scheduleExpr = scheduleKind === "cron" ? String(schedule.expr ?? "") : "";
  const scheduleTz = scheduleKind === "cron" ? String(schedule.tz ?? "") : "";
  const scheduleStaggerMs =
    scheduleKind === "cron" && typeof schedule.staggerMs === "number"
      ? schedule.staggerMs
      : undefined;

  const payloadKind = readKind(payload.kind, ["systemEvent", "agentTurn", "workflowRun"], "payload.kind");
  const payloadInput =
    payloadKind === "workflowRun" && isJsonObject(payload.input)
      ? stringifyJson(payload.input)
      : "";

  const deliveryEnabled = job.delivery !== undefined && job.delivery !== null;
  const deliveryMode = deliveryEnabled
    ? readKind(job.delivery?.mode, ["none", "announce", "webhook"], "delivery.mode")
    : "none";

  const deliveryFailureDestinationEnabled =
    deliveryEnabled && isJsonObject(job.delivery?.failureDestination);
  const deliveryFailureDestination = deliveryFailureDestinationEnabled
    ? (job.delivery?.failureDestination as Record<string, unknown>)
    : {};

  const failureAlertEnabled =
    job.failureAlert !== undefined &&
    job.failureAlert !== null &&
    typeof job.failureAlert === "object" &&
    !Array.isArray(job.failureAlert);

  return {
    name: job.name ?? "",
    description: job.description ?? "",
    enabled: job.enabled ?? false,
    deleteAfterRun: Boolean(job.deleteAfterRun),
    sessionTarget: job.sessionTarget ?? "main",
    wakeMode: job.wakeMode ?? "now",
    schedule: {
      kind: scheduleKind,
      at: scheduleAt,
      everyMs: scheduleEveryMs,
      ...(scheduleAnchorMs !== undefined ? { anchorMs: scheduleAnchorMs } : {}),
      expr: scheduleExpr,
      tz: scheduleTz,
      ...(scheduleStaggerMs !== undefined ? { staggerMs: scheduleStaggerMs } : {}),
    },
    payload: {
      kind: payloadKind,
      text: payloadKind === "systemEvent" ? String(payload.text ?? "") : "",
      message: payloadKind === "agentTurn" ? String(payload.message ?? "") : "",
      model: payloadKind === "agentTurn" ? String(payload.model ?? "") : "",
      fallbacksCsv:
        payloadKind === "agentTurn" && Array.isArray(payload.fallbacks)
          ? payload.fallbacks.filter(isNonEmptyString).join(", ")
          : "",
      thinking: payloadKind === "agentTurn" ? String(payload.thinking ?? "") : "",
      timeoutSeconds:
        payloadKind === "agentTurn" && typeof payload.timeoutSeconds === "number"
          ? payload.timeoutSeconds
          : undefined,
      allowUnsafeExternalContent: payloadKind === "agentTurn" && payload.allowUnsafeExternalContent === true,
      lightContext: payloadKind === "agentTurn" && payload.lightContext === true,
      deliver: payloadKind === "agentTurn" && payload.deliver === true,
      channel: payloadKind === "agentTurn" ? String(payload.channel ?? "") : "",
      to: payloadKind === "agentTurn" ? String(payload.to ?? "") : "",
      bestEffortDeliver: payloadKind === "agentTurn" && payload.bestEffortDeliver === true,
      workflowKey: payloadKind === "workflowRun" ? String(payload.workflowKey ?? "") : "",
      inputJson: payloadInput,
      inputSummary: payloadKind === "workflowRun" ? String(payload.inputSummary ?? "") : "",
      executionClass: payloadKind === "workflowRun" ? String(payload.executionClass ?? "") : "",
    },
    deliveryEnabled,
    delivery: {
      mode: deliveryMode,
      channel: deliveryEnabled ? String(job.delivery?.channel ?? "") : "",
      to: deliveryEnabled ? String(job.delivery?.to ?? "") : "",
      accountId: deliveryEnabled ? String(job.delivery?.accountId ?? "") : "",
      bestEffort: deliveryEnabled && job.delivery?.bestEffort === true,
      failureDestinationEnabled: deliveryFailureDestinationEnabled,
      failureDestination: {
        channel:
          typeof deliveryFailureDestination.channel === "string"
            ? String(deliveryFailureDestination.channel)
            : "",
        to:
          typeof deliveryFailureDestination.to === "string"
            ? String(deliveryFailureDestination.to)
            : "",
        accountId:
          typeof deliveryFailureDestination.accountId === "string"
            ? String(deliveryFailureDestination.accountId)
            : "",
        mode: readOptionalDestinationMode(deliveryFailureDestination.mode),
      },
    },
    failureAlertEnabled,
    failureAlert: {
      after:
        failureAlertEnabled && typeof (job.failureAlert as Record<string, unknown>).after === "number"
          ? ((job.failureAlert as Record<string, unknown>).after as number)
          : undefined,
      channel:
        failureAlertEnabled && typeof (job.failureAlert as Record<string, unknown>).channel === "string"
          ? String((job.failureAlert as Record<string, unknown>).channel)
          : "",
      to:
        failureAlertEnabled && typeof (job.failureAlert as Record<string, unknown>).to === "string"
          ? String((job.failureAlert as Record<string, unknown>).to)
          : "",
      cooldownMs:
        failureAlertEnabled && typeof (job.failureAlert as Record<string, unknown>).cooldownMs === "number"
          ? ((job.failureAlert as Record<string, unknown>).cooldownMs as number)
          : undefined,
      mode: failureAlertEnabled
        ? readOptionalDestinationMode((job.failureAlert as Record<string, unknown>).mode)
        : "",
      accountId:
        failureAlertEnabled && typeof (job.failureAlert as Record<string, unknown>).accountId === "string"
          ? String((job.failureAlert as Record<string, unknown>).accountId)
          : "",
    },
  };
}

export function buildRuntimeCronRecordForSave(
  job: RuntimeCronJobRecordDto,
  state: RuntimeCronEditorState,
): RuntimeCronJobRecord {
  const schedule = buildSchedule(state);
  const payload = buildPayload(state);
  const delivery = buildDelivery(state);
  const failureAlert = buildFailureAlert(state);

  return {
    ...job,
    cronJobId: requireCronJobId(job),
    tenantId: requireNonEmptyString(job.tenantId, "tenantId"),
    name: requireNonEmptyString(state.name, "Name"),
    description: state.description.trim() || undefined,
    enabled: state.enabled,
    deleteAfterRun: state.deleteAfterRun,
    sessionTarget: state.sessionTarget,
    wakeMode: state.wakeMode,
    schedule,
    payload,
    delivery,
    failureAlert,
    updatedAt: new Date().toISOString(),
    scopeKind: getCronScope(job),
    ownerUserId: getCronOwnerUserId(job),
  } as RuntimeCronJobRecord;
}

function stringifyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function requireNonEmptyString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${label} is required.`);
  }
  return value.trim();
}

function requirePositiveNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    throw new Error(`${label} must be a positive number.`);
  }
  return value;
}

function readKind<TKind extends string>(
  value: unknown,
  allowed: TKind[],
  label: string,
): TKind {
  if (typeof value !== "string" || !allowed.includes(value as TKind)) {
    throw new Error(`${label} has an invalid kind.`);
  }
  return value as TKind;
}

function readOptionalDestinationMode(value: unknown): "" | "announce" | "webhook" {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  return readKind(value, ["announce", "webhook"], "destination.mode");
}

function buildSchedule(state: RuntimeCronEditorState): Record<string, unknown> {
  const kind = state.schedule.kind;
  if (kind === "at") {
    return { kind, at: requireNonEmptyString(state.schedule.at, "schedule.at") };
  }
  if (kind === "every") {
    const everyMs = requirePositiveNumber(state.schedule.everyMs, "schedule.everyMs");
    return state.schedule.anchorMs !== undefined
      ? { kind, everyMs, anchorMs: state.schedule.anchorMs }
      : { kind, everyMs };
  }
  return {
    kind,
    expr: requireNonEmptyString(state.schedule.expr, "schedule.expr"),
    ...(state.schedule.tz.trim() ? { tz: state.schedule.tz.trim() } : {}),
    ...(state.schedule.staggerMs !== undefined ? { staggerMs: state.schedule.staggerMs } : {}),
  };
}

function buildPayload(state: RuntimeCronEditorState): Record<string, unknown> {
  const kind = state.payload.kind;
  if (kind === "systemEvent") {
    return { kind, text: requireNonEmptyString(state.payload.text, "payload.text") };
  }
  if (kind === "agentTurn") {
    const fallbacks = state.payload.fallbacksCsv
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    return {
      kind,
      message: requireNonEmptyString(state.payload.message, "payload.message"),
      ...(state.payload.model.trim() ? { model: state.payload.model.trim() } : {}),
      ...(fallbacks.length > 0 ? { fallbacks } : {}),
      ...(state.payload.thinking.trim() ? { thinking: state.payload.thinking.trim() } : {}),
      ...(typeof state.payload.timeoutSeconds === "number"
        ? { timeoutSeconds: state.payload.timeoutSeconds }
        : {}),
      ...(state.payload.allowUnsafeExternalContent ? { allowUnsafeExternalContent: true } : {}),
      ...(state.payload.lightContext ? { lightContext: true } : {}),
      ...(state.payload.deliver ? { deliver: true } : {}),
      ...(state.payload.channel.trim() ? { channel: state.payload.channel.trim() } : {}),
      ...(state.payload.to.trim() ? { to: state.payload.to.trim() } : {}),
      ...(state.payload.bestEffortDeliver ? { bestEffortDeliver: true } : {}),
    };
  }

  const workflowKey = requireNonEmptyString(state.payload.workflowKey, "payload.workflowKey");
  const input = parseOptionalJsonObject(state.payload.inputJson, "payload.input");
  return {
    kind,
    workflowKey,
    ...(input ? { input } : {}),
    ...(state.payload.inputSummary.trim()
      ? { inputSummary: state.payload.inputSummary.trim() }
      : {}),
    ...(state.payload.executionClass.trim()
      ? { executionClass: state.payload.executionClass.trim() }
      : {}),
  };
}

function buildDelivery(state: RuntimeCronEditorState): Record<string, unknown> | undefined {
  if (!state.deliveryEnabled) {
    return undefined;
  }
  const mode = state.delivery.mode;
  const failureDestinationMode =
    state.delivery.failureDestination.mode.trim().length === 0
      ? undefined
      : readKind(
          state.delivery.failureDestination.mode,
          ["announce", "webhook"],
          "delivery.failureDestination.mode",
        );
  const failureDestination = state.delivery.failureDestinationEnabled
    ? {
        ...(state.delivery.failureDestination.channel.trim()
          ? { channel: state.delivery.failureDestination.channel.trim() }
          : {}),
        ...(state.delivery.failureDestination.to.trim()
          ? { to: state.delivery.failureDestination.to.trim() }
          : {}),
        ...(state.delivery.failureDestination.accountId.trim()
          ? { accountId: state.delivery.failureDestination.accountId.trim() }
          : {}),
        ...(failureDestinationMode ? { mode: failureDestinationMode } : {}),
      }
    : undefined;
  return {
    mode,
    ...(state.delivery.channel.trim() ? { channel: state.delivery.channel.trim() } : {}),
    ...(state.delivery.to.trim() ? { to: state.delivery.to.trim() } : {}),
    ...(state.delivery.accountId.trim() ? { accountId: state.delivery.accountId.trim() } : {}),
    ...(state.delivery.bestEffort ? { bestEffort: true } : {}),
    ...(failureDestination ? { failureDestination } : {}),
  };
}

function buildFailureAlert(state: RuntimeCronEditorState): Record<string, unknown> | undefined {
  if (!state.failureAlertEnabled) {
    return undefined;
  }
  const mode =
    state.failureAlert.mode.trim().length === 0
      ? undefined
      : readKind(state.failureAlert.mode, ["announce", "webhook"], "failureAlert.mode");
  return {
    ...(typeof state.failureAlert.after === "number" ? { after: state.failureAlert.after } : {}),
    ...(state.failureAlert.channel.trim()
      ? { channel: state.failureAlert.channel.trim() }
      : {}),
    ...(state.failureAlert.to.trim() ? { to: state.failureAlert.to.trim() } : {}),
    ...(typeof state.failureAlert.cooldownMs === "number"
      ? { cooldownMs: state.failureAlert.cooldownMs }
      : {}),
    ...(mode ? { mode } : {}),
    ...(state.failureAlert.accountId.trim()
      ? { accountId: state.failureAlert.accountId.trim() }
      : {}),
  };
}

function parseOptionalJsonObject(raw: string, label: string): Record<string, unknown> | undefined {
  if (!raw.trim()) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    throw new Error(`${label} must be valid JSON.`, { cause: error });
  }
  if (!isJsonObject(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return parsed;
}
